// SPDX-License-Identifier: AGPL-3.0-or-later

import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import {
	markScreenShareCaptureActive,
	markScreenShareCaptureEnded,
	updateScreenShareDisplayMediaSettings,
} from '@app/features/voice/engine/ScreenShareCaptureDiagnostics';
import {
	type CapturedScreenShareTracks,
	stopMediaTrack,
	stopUnselectedStreamTracks,
} from '@app/features/voice/engine/voice_screen_share_manager/shared';
import ActiveScreenShareSource from '@app/features/voice/state/ActiveScreenShareSource';
import {createScreenGeneratorBridge} from '@app/features/voice/utils/native_screen_capture_bridge/createGeneratorBridge';
import {
	getGeneratorVideoCtor,
	getNativeScreenCaptureApi,
	getVideoFrameCtor,
	type NativeScreenBridgeHandle,
	normalizeNativeScreenCaptureResolution,
} from '@app/features/voice/utils/native_screen_capture_bridge/shared';
import type {NativeScreenCaptureAvailability, NativeScreenCaptureSource} from '@app/types/electron.d';
import type {ScreenShareCaptureOptions} from 'livekit-client';

type DisplayMediaVideoConstraints = MediaTrackConstraints & {
	cursor?: 'always' | 'motion' | 'never';
	displaySurface?: 'browser' | 'monitor' | 'window';
};
type DisplayMediaAudioConstraints = MediaTrackConstraints & {
	restrictOwnAudio?: boolean;
	suppressLocalAudioPlayback?: boolean;
};
type DisplayMediaTrackSettings = MediaTrackSettings & {
	cursor?: 'always' | 'motion' | 'never';
	displaySurface?: 'browser' | 'monitor' | 'window';
};
interface NativeCaptureAvailabilityReader {
	getAvailability?: () => Promise<NativeScreenCaptureAvailability>;
}

async function readNativeCaptureAvailability(
	api: NativeCaptureAvailabilityReader,
): Promise<NativeScreenCaptureAvailability | null> {
	if (typeof api.getAvailability !== 'function') return null;
	return Promise.resolve(api.getAvailability()).catch(() => null);
}

function resolveDisplayMediaCursorCapture(
	displaySurface: DisplayMediaVideoConstraints['displaySurface'],
): 'always' | 'motion' | 'never' {
	return displaySurface === 'window' ? 'never' : 'always';
}

function getRequestedDisplayMediaVideoConstraints(
	options: ScreenShareCaptureOptions | undefined,
): DisplayMediaVideoConstraints | null {
	if (typeof options?.video !== 'object' || !options.video) return null;
	return options.video as DisplayMediaVideoConstraints;
}

export function resolveCapturedDisplayMediaCursorCapture(
	track: Pick<MediaStreamTrack, 'getSettings'>,
	options?: ScreenShareCaptureOptions,
): 'always' | 'motion' | 'never' {
	const requestedVideo = getRequestedDisplayMediaVideoConstraints(options);
	const requestedCursor = requestedVideo?.cursor;
	const requestedDisplaySurface = requestedVideo?.displaySurface;
	if (requestedCursor && requestedCursor !== resolveDisplayMediaCursorCapture(requestedDisplaySurface)) {
		return requestedCursor;
	}
	const settings = track.getSettings() as DisplayMediaTrackSettings;
	return resolveDisplayMediaCursorCapture(settings.displaySurface ?? requestedDisplaySurface);
}

export function getDisplayMediaOptions(options?: ScreenShareCaptureOptions): DisplayMediaStreamOptions {
	let videoConstraints: MediaTrackConstraints | boolean = options?.video ?? true;
	const resolution = options?.resolution;
	if (resolution && resolution.width > 0 && resolution.height > 0) {
		videoConstraints = typeof videoConstraints === 'boolean' ? {} : videoConstraints;
		videoConstraints = {
			...videoConstraints,
			width: {ideal: resolution.width},
			height: {ideal: resolution.height},
			frameRate: resolution.frameRate,
		};
	}
	const base = (typeof videoConstraints === 'boolean' ? {} : videoConstraints) as DisplayMediaVideoConstraints;
	videoConstraints = {
		...base,
		cursor: base.cursor ?? resolveDisplayMediaCursorCapture(base.displaySurface),
	} as MediaTrackConstraints;
	let audioConstraints: DisplayMediaStreamOptions['audio'] = options?.audio ?? false;
	if (audioConstraints && (options?.restrictOwnAudio === true || options?.suppressLocalAudioPlayback === true)) {
		const baseAudio = typeof audioConstraints === 'object' ? audioConstraints : {};
		audioConstraints = {
			...baseAudio,
			...(options?.restrictOwnAudio === true ? {restrictOwnAudio: true} : {}),
			...(options?.suppressLocalAudioPlayback === true ? {suppressLocalAudioPlayback: true} : {}),
		} as DisplayMediaAudioConstraints;
	}
	return {
		audio: audioConstraints,
		video: videoConstraints,
		controller: options?.controller,
		selfBrowserSurface: options?.selfBrowserSurface,
		surfaceSwitching: options?.surfaceSwitching,
		systemAudio: options?.systemAudio,
		windowAudio: options?.windowAudio,
		monitorTypeSurfaces: options?.monitorTypeSurfaces,
		preferCurrentTab: options?.preferCurrentTab,
	} as DisplayMediaStreamOptions;
}

export async function createDisplayScreenShareTracks(
	options?: ScreenShareCaptureOptions,
): Promise<CapturedScreenShareTracks> {
	if (!navigator.mediaDevices.getDisplayMedia) {
		throw new Error('getDisplayMedia not supported');
	}
	const stream = await navigator.mediaDevices.getDisplayMedia(getDisplayMediaOptions(options));
	const videoTrack = stream.getVideoTracks()[0];
	if (!videoTrack) {
		stream.getTracks().forEach(stopMediaTrack);
		throw new Error('No video track found in screen share capture');
	}
	if (options?.contentHint) {
		videoTrack.contentHint = options.contentHint;
	}
	updateScreenShareDisplayMediaSettings(videoTrack, {
		sourceId: ActiveScreenShareSource.getSourceId(),
	});
	const cursor = resolveCapturedDisplayMediaCursorCapture(videoTrack, options);
	if ((videoTrack.getSettings() as DisplayMediaTrackSettings).cursor !== cursor) {
		await videoTrack.applyConstraints({cursor} as MediaTrackConstraints).catch(() => undefined);
	}
	const audioTrack = stream.getAudioTracks()[0];
	stopUnselectedStreamTracks(stream, [videoTrack, audioTrack]);
	return {
		videoTrack,
		audioTrack,
	};
}

export interface NativeScreenShareOptions {
	source: NativeScreenCaptureSource;
	resolution?: ScreenShareCaptureOptions['resolution'];
	contentHint?: ScreenShareCaptureOptions['contentHint'];
	audioTrack?: MediaStreamTrack;
	fallbackThumbnailDataUrl?: string;
}

export async function isNativeScreenCaptureAvailable(): Promise<boolean> {
	const electronApi = getElectronAPI();
	const api = electronApi?.nativeScreenCapture ?? getNativeScreenCaptureApi();
	if (!api) return false;
	if (!getGeneratorVideoCtor() || !getVideoFrameCtor()) return false;
	try {
		const availability = await api.getAvailability();
		return availability.available;
	} catch {
		return false;
	}
}

export async function startNativeCaptureForEngine(
	options: NativeScreenShareOptions,
): Promise<{captureId: string; width: number; height: number}> {
	const api = getNativeScreenCaptureApi();
	if (!api) {
		throw new Error('Native screen capture API unavailable');
	}
	const {source, resolution} = options;
	const nativeResolution = normalizeNativeScreenCaptureResolution(resolution);
	const availability = await readNativeCaptureAvailability(api);
	const startResult = await api.start({
		sourceId: source.id,
		sourceKind: source.kind,
		width: nativeResolution?.width,
		height: nativeResolution?.height,
		frameRate: resolution?.frameRate,
		injectionMethod: source.kind === 'game' ? DeveloperOptions.gameCaptureInjectionMethod : undefined,
	});
	markScreenShareCaptureActive({
		method: 'native-voice-engine-screen-capture',
		sourceId: source.id,
		sourceKind: source.kind,
		captureId: startResult.captureId,
		nativeAvailability: availability,
	});
	return {
		captureId: startResult.captureId,
		width: startResult.width || source.width,
		height: startResult.height || source.height,
	};
}

export async function stopNativeCaptureForEngine(captureId: string): Promise<void> {
	const api = getNativeScreenCaptureApi();
	if (!api) return;
	await api.stop(captureId).catch(() => undefined);
}

export async function createNativeScreenShareTracks(
	options: NativeScreenShareOptions,
): Promise<CapturedScreenShareTracks> {
	const api = getNativeScreenCaptureApi();
	if (!api) {
		throw new Error('Native screen capture API unavailable');
	}
	if (!getElectronAPI()) {
		throw new Error('Electron API unavailable');
	}
	const {source, resolution, contentHint, audioTrack} = options;
	const nativeResolution = normalizeNativeScreenCaptureResolution(resolution);
	const availability = await readNativeCaptureAvailability(api);
	const startResult = await api.start({
		sourceId: source.id,
		sourceKind: source.kind,
		width: nativeResolution?.width,
		height: nativeResolution?.height,
		frameRate: resolution?.frameRate,
		injectionMethod: source.kind === 'game' ? DeveloperOptions.gameCaptureInjectionMethod : undefined,
	});
	markScreenShareCaptureActive({
		method: 'native-screen-capture',
		sourceId: source.id,
		sourceKind: source.kind,
		captureId: startResult.captureId,
		nativeAvailability: availability,
	});
	let bridge: NativeScreenBridgeHandle;
	try {
		bridge = await createScreenGeneratorBridge(startResult.captureId, {
			initialFrame: startResult.initialFrame,
			fallbackWidth: startResult.width || source.width,
			fallbackHeight: startResult.height || source.height,
			fallbackThumbnailDataUrl: options.fallbackThumbnailDataUrl,
		});
	} catch (error) {
		await api.stop(startResult.captureId).catch(() => undefined);
		markScreenShareCaptureEnded('native-screen-capture-bridge-failed');
		if (audioTrack) {
			try {
				audioTrack.stop();
			} catch {}
		}
		throw error;
	}
	const track = bridge.track;
	if (contentHint) {
		track.contentHint = contentHint;
	}
	const originalStop = track.stop.bind(track);
	let restored = false;
	const patchedStop = function patchedNativeScreenShareTrackStop(this: MediaStreamTrack): void {
		if (!restored) {
			restored = true;
			void bridge.cleanup();
			if (audioTrack) {
				try {
					audioTrack.stop();
				} catch {}
			}
		}
		originalStop();
	};
	try {
		Object.defineProperty(track, 'stop', {
			value: patchedStop,
			configurable: true,
			writable: true,
		});
	} catch {
		track.stop = patchedStop;
	}
	return {videoTrack: track, audioTrack};
}
